// src/layout/Sidebar.tsx
import React from 'react';
import { Menu, Layout, Button } from 'antd';
import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getFilteredMenuRoutes, getDashboardRoute, getCurrentContext } from '../utils/menuUtils';

const { Sider } = Layout;

const contextLabels: Record<ReturnType<typeof getCurrentContext>, string> = {
  accounts: 'Accounts Application',
  finance: 'Finance Application',
  'master-data': 'Master Data Application',
  leads: 'Leads Application',
  default: 'Accounts Application',
};

interface SidebarProps {
  collapsed: boolean;
  onToggleSidebar?: () => void;
  appRoutes: Array<any>;
  appName?: string;
}

const Sidebar: React.FC<SidebarProps> = ({
  collapsed,
  onToggleSidebar,
  appRoutes }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get filtered routes based on current context
  const filteredRoutes = getFilteredMenuRoutes(appRoutes, location.pathname);
  const currentContext = getCurrentContext(location.pathname);
  const contextLabel = contextLabels[currentContext];

  const getSelectedMenuKey = (pathname: string): string => {
    const matched = filteredRoutes
      .filter((r) => r.isMenu)
      .sort((a, b) => b.path.length - a.path.length)
      .find((r) => pathname.startsWith(r.path));
    return matched?.path || getDashboardRoute(pathname);
  };

  const menuItems = filteredRoutes
    .filter((route) => route.isMenu)
    .sort((a, b) => (a.menuOrder ?? 999) - (b.menuOrder ?? 999))
    .map((route) => ({
      key: route.path,
      icon: <FontAwesomeIcon icon={route.icon} />,
      label: route.label,
    }));

  return (
    <Sider
      theme="light"
      width={200}
      collapsedWidth={80}
      collapsed={collapsed}
      className="fixed top-16 left-0 h-[calc(100vh-64px)] z-40 bg-[#f7f9fc] border-r border-[#e5e8ec] shadow-sm"
    >
      <div className="flex flex-col h-full justify-between">
        {/* Context Header */}
        {!collapsed && (
          <div className="p-3 border-b border-[#e5e8ec] bg-white">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              {contextLabel}
            </div>
          </div>
        )}
        
        {/* Top: Menu */}
        <div className="flex-1 overflow-auto">
          <Menu
            mode="inline"
            selectedKeys={[getSelectedMenuKey(location.pathname)]}
            items={menuItems}
            onClick={({ key }) => navigate(key)}
          />
        </div>

        {/* Bottom: Collapse Button */}
        {onToggleSidebar && (
          <div className="p-2 border-t border-gray-300 text-right">
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={onToggleSidebar}
              title={collapsed ? 'Expand Menu' : 'Collapse Menu'}
            />
          </div>
        )}
      </div>
    </Sider>
  );
};

export default Sidebar;
